import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchIndex, fetchMedia } from '../api/contentApi';
import { typeInfo } from '../content/contentTypes';
import { applyListingSeo } from '../content/seo';
import ContentCard from '../components/ContentCard';
import StatusMessage from '../components/StatusMessage';
import LoadError from '../components/LoadError';
import { SITE_NAME } from '../siteConfig';
import useAsync from '../useAsync';
import { PageHero } from '../../components/ui';

export default function InsightsPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') || '';
  const tag = params.get('tag') || '';
  const type = params.get('type') || '';
  const { loading, error, data, reload } = useAsync(() => Promise.all([fetchIndex(), fetchMedia()]), []);
  const [entries, media] = data || [[], {}];

  useEffect(() => {
    applyListingSeo({ siteName: SITE_NAME, description: 'Guides, case studies and news from our engineering team.' });
  }, []);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const types = useMemo(() => [...new Set(entries.map((e) => e.type))], [entries]);
  const tags = useMemo(() => [...new Set(entries.flatMap((e) => e.tags || []))].sort(), [entries]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return entries.filter((e) => {
      if (type && e.type !== type) return false;
      if (tag && !(e.tags || []).includes(tag)) return false;
      if (!q) return true;
      return [e.title, e.excerpt, e.authorName, e.categoryName, ...(e.tags || [])]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(q));
    });
  }, [entries, query, tag, type]);

  const filtering = Boolean(query || tag || type);

  return (
    <>
    <PageHero
      eyebrow="Insights"
      title="Insights & articles"
      text="Guides, case studies and news from our engineering team."
    />
    <div className="blog-container home">

      <div className="filters">
        <input
          type="search"
          className="search"
          placeholder="Search posts..."
          value={query}
          onChange={(e) => setParam('q', e.target.value)}
          aria-label="Search posts"
        />
        {types.length > 1 && (
          <div className="chips" role="group" aria-label="Filter by type">
            <button type="button" className={`chip${!type ? ' active' : ''}`} onClick={() => setParam('type', '')}>
              All types
            </button>
            {types.map((t) => (
              <button key={t} type="button" className={`chip${type === t ? ' active' : ''}`} onClick={() => setParam('type', t)}>
                {typeInfo(t).label}
              </button>
            ))}
          </div>
        )}
        {tags.length > 0 && (
          <div className="chips" role="group" aria-label="Filter by tag">
            {tags.map((t) => (
              <button
                key={t}
                type="button"
                className={`chip${tag === t ? ' active' : ''}`}
                aria-pressed={tag === t}
                onClick={() => setParam('tag', tag === t ? '' : t)}
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && <StatusMessage title="Loading posts..." />}
      {error && <LoadError error={error} onRetry={reload} />}
      {!loading && !error && filtered.length === 0 && (
        <StatusMessage title={entries.length ? 'No posts match your filters' : 'No posts yet'}>
          {entries.length ? 'Try a different search or tag.' : 'Articles will appear here once they are published.'}
        </StatusMessage>
      )}

      {filtered.length > 0 && (
        <div className="card-grid">
          {filtered.map((entry, i) => (
            <ContentCard key={entry.id || entry.slug} entry={entry} media={media} featured={!filtering && i === 0} />
          ))}
        </div>
      )}
    </div>
    </>
  );
}
